import { UpDownIcon } from '@chakra-ui/icons';
import {
  Button,
  FlexProps,
  HStack,
  Icon,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Text,
} from '@chakra-ui/react';
import {
  CogIcon,
  IdentificationIcon,
  LibraryIcon,
  LogoutIcon,
  UserIcon,
} from '@heroicons/react/outline';
import { Link, useLocation } from 'react-router-dom';
import { useTranslations } from 'use-intl';

import { routes } from '@blockpulse3/data/shared';
import { UserRole } from '@blockpulse3/graphql/hooks';
import { useUser } from '@blockpulse3/web-client/auth';

type Props = FlexProps;

export function SettingsMenu(): JSX.Element {
  const { user } = useUser();
  const location = useLocation();
  const i18nSettingsMenu = useTranslations('SettingsMenu');

  const handleLogout = (): void => {
    localStorage.removeItem('token');
    window.location.href = routes.login.href;
  };

  const isSuperAdmin = user?.roles.includes(UserRole.SUPERADMIN);
  const isManaging: boolean = location.pathname?.includes('/manage');

  const spaceLabelButton = isManaging ? 'UserSpace' : 'AdminSpace';
  const adminSpaceIcon: JSX.Element = isManaging ? (
    <UserIcon height="18" width="18" />
  ) : (
    <LibraryIcon height="18" width="18" />
  );

  return (
    <Menu matchWidth>
      <MenuButton as={Button} data-cy="settings-menu" variant="menu-button" w="full">
        <HStack w="full">
          <Icon as={CogIcon} boxSize="18px" />
          <Text>{i18nSettingsMenu('SettingsMenuTitle')}</Text>
          <UpDownIcon boxSize="4" ml="auto" />
        </HStack>
      </MenuButton>
      <MenuList color="gray.900" p="2" shadow="lg">
        {isSuperAdmin && (
          <Link to={isManaging ? routes.me.href : routes.admin.href}>
            <MenuItem fontWeight="semibold" px="2" rounded="md">
              <HStack>
                {adminSpaceIcon}
                <Text>{i18nSettingsMenu(spaceLabelButton)}</Text>
              </HStack>
            </MenuItem>
          </Link>
        )}
        <Link to={routes.me.settings.href}>
          <MenuItem fontWeight="semibold" px="2" rounded="md">
            <HStack>
              <IdentificationIcon height="18" width="18" />
              <Text>{i18nSettingsMenu('SettingItem')}</Text>
            </HStack>
          </MenuItem>
        </Link>
        <MenuItem data-cy="logout" fontWeight="semibold" px="2" rounded="md" onClick={handleLogout}>
          <HStack>
            <LogoutIcon height="18" width="18" />
            <Text>{i18nSettingsMenu('LogoutItem')}</Text>
          </HStack>
        </MenuItem>
      </MenuList>
    </Menu>
  );
}

export type SettingsMenuProps = Props;
